import { APP } from './tokens';

// ─── Quick Action data ────────────────────────────

interface QuickAction {
  label: string;
  icon: React.ReactNode;
  action: string;
}

const QUICK_ACTIONS: QuickAction[] = [
  {
    label: 'Send for signature',
    action: 'send_signature',
    icon: (
      <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
        <path d="M4 9.5 C4 9.5 3.5 5.5 6.5 5 C9.5 4.5 9 8.5 9 8.5" stroke={APP.textPrimary} strokeWidth="1.3" strokeLinecap="round" />
        <path d="M3 9.5h7" stroke={APP.textPrimary} strokeWidth="1.3" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    label: 'Collect signatures',
    action: 'multi_party_sign',
    icon: (
      <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
        <circle cx="4" cy="4.5" r="2" stroke={APP.textPrimary} strokeWidth="1.2" />
        <circle cx="9" cy="4.5" r="2" stroke={APP.textPrimary} strokeWidth="1.2" />
        <path d="M2 11c.5-2 2.5-3 4.5-3h1.5c2 0 3.5 1 4 3" stroke={APP.textPrimary} strokeWidth="1.2" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    label: 'Place signing fields',
    action: 'place_sign_fields',
    icon: (
      <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
        <rect x="2" y="2" width="9" height="9" rx="1.5" stroke={APP.textPrimary} strokeWidth="1.2" />
        <path d="M5 6h3M6.5 4.5v3" stroke={APP.textPrimary} strokeWidth="1.2" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    label: 'Signing dashboard',
    action: 'view_signatures',
    icon: (
      <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
        <rect x="2" y="2.5" width="9" height="8" rx="1.5" stroke={APP.textPrimary} strokeWidth="1.2" />
        <path d="M4 6h5M4 8.5h3" stroke={APP.textPrimary} strokeWidth="1.2" strokeLinecap="round" />
      </svg>
    ),
  },
];

// ─── Starter template cards (signature-first) ─────

interface StarterTemplate {
  title: string;
  description: string;
  emoji: string;
}

const STARTER_TEMPLATES: StarterTemplate[] = [
  {
    title: 'Mutual NDA',
    description: 'Standard mutual NDA wording; add signers and send when your file is ready.',
    emoji: '📄',
  },
  {
    title: 'Offer letter',
    description: 'Candidate acceptance plus employer signature — common hiring package.',
    emoji: '✉️',
  },
  {
    title: 'Vendor agreement',
    description: 'Master services or vendor terms lined up for counterparty e-sign.',
    emoji: '📝',
  },
];

// ─── Sub-components ───────────────────────────────

function QuickActionPill({ action, onClick }: { action: QuickAction; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        borderRadius: 999,
        paddingBlock: 7,
        paddingInline: 14,
        gap: 6,
        backgroundColor: APP.bgQuickAction,
        border: `1px solid ${APP.borderQuickAction}`,
        cursor: 'pointer',
        fontFamily: APP.fontFamily,
        boxSizing: 'border-box',
      }}
    >
      {action.icon}
      <span style={{ fontSize: 11, fontWeight: 500, color: APP.textPrimary, lineHeight: '14px' }}>
        {action.label}
      </span>
    </button>
  );
}

function StarterTemplateCard({ template, onClick }: { template: StarterTemplate; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        flexShrink: 1,
        flexBasis: '0%',
        borderRadius: 10,
        padding: 14,
        gap: 10,
        backgroundColor: APP.bgCardBody,
        border: `1px solid ${APP.borderCard}`,
        cursor: 'pointer',
        fontFamily: APP.fontFamily,
        textAlign: 'left',
        boxSizing: 'border-box',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div
          style={{
            width: 28,
            height: 28,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            borderRadius: 8,
            backgroundColor: APP.bgWorkflowIcon,
            fontSize: 12,
            lineHeight: '16px',
          }}
        >
          {template.emoji}
        </div>
        <span style={{ fontSize: 13, fontWeight: 600, color: APP.textPrimary, lineHeight: '16px' }}>
          {template.title}
        </span>
      </div>
      <span style={{ fontSize: 12, lineHeight: '18px', color: APP.textDescription }}>
        {template.description}
      </span>
    </button>
  );
}

// ─── Main EmptyState ──────────────────────────────

interface EmptyStateProps {
  onQuickAction: (action: string) => void;
  onStarterTemplateClick: (title: string) => void;
  onViewSignatures: () => void;
  queuedFileCount?: number;
  primaryFileName?: string | null;
}

export default function EmptyState({
  onQuickAction,
  onStarterTemplateClick,
  onViewSignatures,
  queuedFileCount = 0,
  primaryFileName = null,
}: EmptyStateProps) {
  const hasQueuedFiles = queuedFileCount > 0;
  const dropZoneTitle = hasQueuedFiles
    ? queuedFileCount === 1
      ? `${primaryFileName ?? 'Your file'} is ready`
      : `${queuedFileCount} files are ready`
    : 'Drop a file to get started';
  const dropZoneSubtitle = hasQueuedFiles
    ? 'Use the input below to tell Corner what to do with them'
    : 'or describe what you need in the input below';

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        paddingTop: 'calc(50vh - 280px)',
        paddingLeft: 40,
        paddingRight: 40,
        paddingBottom: 40,
        gap: 20,
      }}
    >
      {/* Drop zone */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          borderRadius: 16,
          padding: '48px 64px',
          gap: 12,
          backgroundColor: APP.bgDropZone,
          border: `2px dashed ${APP.borderDropZone}`,
        }}
      >
        {/* Download arrow icon */}
        <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
          <path d="M18 4v16M11 13l7 8 7-8M6 26h24" stroke="#8B7355" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <span style={{ fontSize: 15, fontWeight: 600, color: APP.textSecondary, lineHeight: '18px', fontFamily: APP.fontFamily }}>
          {dropZoneTitle}
        </span>
        <span style={{ fontSize: 12, color: APP.textSecondary, lineHeight: '16px', textAlign: 'center', fontFamily: APP.fontFamily }}>
          {dropZoneSubtitle}
        </span>
      </div>

      {/* Quick action pills */}
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 8 }}>
        {QUICK_ACTIONS.map((action) => (
          <QuickActionPill key={action.action} action={action} onClick={() => onQuickAction(action.action)} />
        ))}
      </div>

      {/* Starter templates */}
      <div style={{ display: 'flex', flexDirection: 'column', maxWidth: 680, width: '100%', marginTop: 12, gap: 12 }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span
            style={{
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: '0.08em',
              textTransform: 'uppercase' as const,
              color: APP.textMuted,
              lineHeight: '14px',
              fontFamily: APP.fontFamily,
            }}
          >
            Starter templates
          </span>
          <button
            type="button"
            onClick={onViewSignatures}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 0,
              fontSize: 12,
              fontWeight: 500,
              color: APP.textViewAll,
              lineHeight: '16px',
              fontFamily: APP.fontFamily,
            }}
          >
            View signatures
          </button>
        </div>

        {/* Cards row */}
        <div style={{ display: 'flex', gap: 12 }}>
          {STARTER_TEMPLATES.map((t) => (
            <StarterTemplateCard key={t.title} template={t} onClick={() => onStarterTemplateClick(t.title)} />
          ))}
        </div>
      </div>
    </div>
  );
}
